import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { LogOut, LayoutDashboard, FileText, Settings, BarChart3, User, Menu, Users, Inbox } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';
import { SupportCaseModal } from './SupportCaseModal';

export const DashboardLayout = ({ children, fullWidth }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/templates', label: 'Templates', icon: FileText },
    { path: '/submissions', label: 'Submissions', icon: Inbox },
    { path: '/team', label: 'Team', icon: Users },
    { path: '/billing', label: 'Billing', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className={`${fullWidth ? 'w-full' : 'max-w-7xl mx-auto'} px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between`}>
          <Link to="/dashboard" className="flex items-center space-x-2.5" data-testid="logo-link">
            <img src="/formlio_final_logo.png" alt="Formlio" className={`${fullWidth ? 'h-10 w-10' : 'h-8 w-8'} object-contain rounded-md`} />
            <span className={`${fullWidth ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'} font-heading font-bold text-slate-900`}>Formlio</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <Button 
                key={item.path}
                variant="ghost" 
                size="sm" 
                asChild 
                className={isActive(item.path) ? 'bg-slate-100' : ''}
                data-testid={`${item.label.toLowerCase()}-nav-btn`}
              >
                <Link to={item.path} className="flex items-center space-x-2">
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              </Button>
            ))}

            {/* Support Case Button */}
            <SupportCaseModal />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center space-x-2" data-testid="user-menu-trigger">
                  <User className="w-4 h-4" />
                  <span className="max-w-24 truncate">{user?.full_name || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="profile-menu-item">
                  <Link to="/profile" className="flex items-center space-x-2 cursor-pointer">
                    <User className="w-4 h-4" />
                    <span>Profile Settings</span>
                  </Link>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <>
                    <DropdownMenuItem asChild data-testid="admin-menu-item">
                      <Link to="/admin" className="flex items-center space-x-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        <span>Admin Panel</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center space-x-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] p-0">
                <SheetHeader className="px-4 py-4 border-b">
                  <SheetTitle className="flex items-center space-x-2">
                    <img src="/formlio_final_logo.png" alt="Formlio" className="h-8 w-8 object-contain rounded-md" />
                    <span>Formlio</span>
                  </SheetTitle>
                </SheetHeader>
                
                {/* User Info */}
                <div className="px-4 py-3 bg-slate-50 border-b">
                  <p className="font-medium text-slate-900 truncate">{user?.full_name || 'User'}</p>
                  <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                </div>

                {/* Navigation Links */}
                <nav className="py-2">
                  {navItems.map((item) => (
                    <SheetClose asChild key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                          isActive(item.path)
                            ? 'bg-indigo-50 text-indigo-600 font-medium'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </SheetClose>
                  ))}
                  
                  {user?.is_admin && (
                    <SheetClose asChild>
                      <Link
                        to="/admin"
                        className={`flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                          isActive('/admin')
                            ? 'bg-indigo-50 text-indigo-600 font-medium'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Settings className="w-5 h-5" />
                        <span>Admin Panel</span>
                      </Link>
                    </SheetClose>
                  )}
                </nav>

                {/* Support Case - Mobile */}
                <div className="px-4 py-3 border-t">
                  <SupportCaseModal />
                </div>

                {/* Logout */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
                  <SheetClose asChild>
                    <Button 
                      variant="outline" 
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <main className={fullWidth ? "px-4 sm:px-6 py-4" : "max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8"}>
        {children}
      </main>
    </div>
  );
};